import * as React from 'react';
import * as classNames from 'classnames';
import * as I18n from 'i18n-js';

import FacetOption from './facet_option';
import FilterLink from './filter_link';
import { FilterOption } from './search_filters';
import experiments from '../experiments';
import { withUrlContext, IUrl } from '../url_context';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { MParticleEventName } from '../elog/mparticle_types';
import { trackEvent } from '../elog/mparticle_tracker';
import ProximityFacet, { PROXIMITY_PARAM, getProximityParams } from './proximity_facet';
import { useUser, useExpEnabled } from '../user_hooks';

interface IExternalProps {
  option: FilterOption;
  checkbox?: boolean;
  children?: JSX.Element;
  childActive?: boolean;
  url?: IUrl;
  trackClick?: boolean;
}

type IProps = IExternalProps;

function onLinkClick(evt, zeroCount: boolean) {
  if (zeroCount) {
    evt.preventDefault();
  }
}

function clickedFilterLink(props: IProps, user) {
  if (props.trackClick && props.option.paramName === 'trait_values') {
    trackEvent({
      experiments: formatExperimentsForEventAttributes(user, [
        experiments.MARKETPLACE_TRAIT_FILTERS,
      ]),
      componentName: 'SelectFacetOption',
      eventName: MParticleEventName.ClickedTraitFilter,
      displayStyle: props.option.name,
      selected: !props.option.selected,
    });
  }
}

function proximityFacetEvent(props: IProps, user) {
  return {
    eventName: MParticleEventName.ClickedMarketplaceFilter,
    componentName: 'ProximityFacet',
    experiments: formatExperimentsForEventAttributes(user, [
      experiments.PROXIMITY_FILTER_MARKETPLACE,
    ]),
    selected: !props.option.selected,
    postalCode: getProximityParams().storedPostalCode,
    distance: getProximityParams().storedDistance,
  };
}

function viewingSearchRollup(props: IProps, searchRollupEnabled: boolean) {
  return searchRollupEnabled && props.url?.pathname === '/marketplace';
}

function linkParams(props: IProps) {
  const useUnset = props.option.selected && props.checkbox;
  const values = useUnset ? props.option.unsetValues : props.option.setValues;

  return { [props.option.paramName]: values };
}

function renderCount(props: IProps, searchRollupEnabled: boolean) {
  if (!props.option.count || viewingSearchRollup(props, searchRollupEnabled)) {
    return null;
  }

  return (
    <div className="facet__option__count">
      ({I18n.toNumber(props.option.count.value, { precision: 0 })})
    </div>
  );
}

export function SelectFacetOption(props: IProps) {
  const { option } = props;
  const linkClass = classNames({
    facet__link: true,
    'facet__link--checkbox': props.checkbox,
    'facet__link--active': option.selected,
  });
  const zeroCount = option.count?.value === 0;
  const user = useUser();
  const searchRollupEnabled = useExpEnabled(experiments.NEO_MARKETPLACE);

  if (option.paramName === PROXIMITY_PARAM) {
    return (
      <ProximityFacet
        option={option}
        linkParams={linkParams(props)}
        clickEvent={proximityFacetEvent(props, user)}
      />
    );
  }

  return (
    <FacetOption
      active={option.selected}
      childActive={props.childActive}
      disabled={zeroCount}
      paramName={option.paramName}
    >
      <FilterLink
        onClick={(evt) => onLinkClick(evt, zeroCount)}
        className={linkClass}
        params={linkParams(props)}
        data-checked={option.selected || null}
        data-disabled={zeroCount || null}
        rel="nofollow"
        trackInteraction={() => clickedFilterLink(props, user)}
      >
        <div>
          <span className="mr-space">{option.name}</span>
          {renderCount(props, searchRollupEnabled)}
        </div>
      </FilterLink>
      {props.children}
    </FacetOption>
  );
}

export default withUrlContext(SelectFacetOption);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/components/select_facet_option.tsx